import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { FadeUp, SectionTag, EASE } from "../components/site/Reveal";

const TRADES = ["Residential Cleaning", "Commercial Cleaning", "Window Cleaning", "Pressure Washing", "Handyman Services", "Maintenance", "Mobile Detailing", "Landscaping", "Painting", "Junk Removal", "Other Service Business"];
const TEAM = ["Just me", "2-3 people", "4-10 people", "10+ people"];
const REVENUE = ["Under $50k", "$50k-$150k", "$150k-$500k", "$500k+"];
const FRICTION = ["Leads slipping through", "Slow or forgotten quotes", "Scheduling chaos", "Late invoices", "Chasing payments", "No follow-up system", "Drowning in admin hours"];

const STEPS = ["Trade & Team", "Friction Points", "Scale & Stack", "Contact"];

export default function Apply() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    trade: "", team_size: "", friction_points: [], revenue_tier: "",
    software_stack: "", name: "", email: "", phone: "", business_name: "", notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFriction = (p) =>
    setForm((f) => ({
      ...f,
      friction_points: f.friction_points.includes(p)
        ? f.friction_points.filter((x) => x !== p)
        : [...f.friction_points, p],
    }));

  const canNext =
    (step === 0 && form.trade && form.team_size) ||
    (step === 1 && form.friction_points.length > 0) ||
    (step === 2 && form.revenue_tier) ||
    (step === 3 && form.name.length > 1 && /\S+@\S+\.\S+/.test(form.email) && form.phone.length > 6 && form.business_name.length > 1);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/apply", form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-navy min-h-screen pt-16 flex items-center" data-testid="apply-success">
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <CheckCircle2 size={56} className="text-safety mx-auto" />
          <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl mt-8 tracking-tight">
            Application received.
          </h1>
          <p className="text-slate-300 mt-6 leading-relaxed">
            Every application is reviewed by hand. Expect an answer within
            <strong className="text-white"> 2 business days</strong> — and a confirmation
            email is on its way to <strong className="text-white">{form.email}</strong>.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-10">
            [STATUS: NEW // QUEUE POSITION SECURED]
          </p>
        </div>
      </div>
    );
  }

  const Opt = ({ active, onClick, children, id }) => (
    <button
      type="button"
      data-testid={id}
      onClick={onClick}
      className={`px-4 py-3 border text-sm font-medium text-left transition-all ${
        active
          ? "border-safety bg-safety/10 text-white"
          : "border-white/15 text-slate-300 hover:border-white/40"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="bg-navy min-h-screen pt-16 blueprint-grid grain" data-testid="apply-page">
      <div className="max-w-3xl mx-auto px-5 py-20 sm:py-28">
        <FadeUp>
          <SectionTag dark>Application // Reviewed by hand</SectionTag>
          <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl mt-6">
            Apply for the Blueprint.
          </h1>
          <p className="text-slate-400 mt-4 leading-relaxed">
            Four short steps. No payment now — you only pay if you're accepted.
          </p>
        </FadeUp>

        <div className="flex gap-2 mt-10 mb-10" data-testid="apply-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 ${i <= step ? "bg-safety" : "bg-white/10"} transition-colors`} />
              <p className={`font-mono text-[10px] uppercase tracking-widest mt-2 ${i <= step ? "text-safety" : "text-slate-600"}`}>
                0{i + 1} {s}
              </p>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="bg-charcoal border border-white/10 p-6 sm:p-10"
          >
            {step === 0 && (
              <div data-testid="apply-step-1">
                <h2 className="font-display font-bold text-white text-xl mb-6">What kind of work do you do?</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-8">
                  {TRADES.map((t) => (
                    <Opt key={t} id={`apply-trade-${t.toLowerCase().replace(/[\s-]+/g, "-")}`} active={form.trade === t} onClick={() => set("trade", t)}>{t}</Opt>
                  ))}
                </div>
                <h3 className="font-display font-bold text-white text-lg mb-4">Who's on the crew?</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {TEAM.map((t) => (
                    <Opt key={t} id={`apply-team-${t.toLowerCase().replace(/[\s+]+/g, "-")}`} active={form.team_size === t} onClick={() => set("team_size", t)}>{t}</Opt>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="apply-step-2">
                <h2 className="font-display font-bold text-white text-xl mb-2">Where does the back office hurt?</h2>
                <p className="text-slate-400 text-sm mb-6">Pick everything that's costing you time or money.</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {FRICTION.map((f) => (
                    <Opt key={f} id={`apply-friction-${f.toLowerCase().replace(/[\s-]+/g, "-")}`} active={form.friction_points.includes(f)} onClick={() => toggleFriction(f)}>{f}</Opt>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="apply-step-3">
                <h2 className="font-display font-bold text-white text-xl mb-6">Annual revenue, roughly?</h2>
                <div className="grid grid-cols-2 gap-2.5 mb-8">
                  {REVENUE.map((r) => (
                    <Opt key={r} id={`apply-revenue-${r.toLowerCase().replace(/[\s$+]+/g, "-")}`} active={form.revenue_tier === r} onClick={() => set("revenue_tier", r)}>{r}</Opt>
                  ))}
                </div>
                <label className="block text-slate-300 text-sm font-medium mb-2">What are you running the business on today? (optional)</label>
                <input
                  data-testid="apply-software-input"
                  value={form.software_stack}
                  onChange={(e) => set("software_stack", e.target.value)}
                  placeholder="e.g. Paper + texts, spreadsheets, Jobber, QuickBooks…"
                  className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none transition-colors"
                />
              </div>
            )}

            {step === 3 && (
              <div data-testid="apply-step-4" className="space-y-5">
                <h2 className="font-display font-bold text-white text-xl">Where do we send the decision?</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Your name</label>
                    <input data-testid="apply-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                      className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Business name</label>
                    <input data-testid="apply-business-input" value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                      className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Email</label>
                    <input data-testid="apply-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                      className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Phone</label>
                    <input data-testid="apply-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                      className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Anything else we should know? (optional)</label>
                  <textarea data-testid="apply-notes-input" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none resize-none" />
                </div>
              </div>
            )}

            <div className="flex justify-between items-center mt-10 pt-6 border-t border-white/10">
              <button
                data-testid="apply-back-btn"
                onClick={() => setStep((s) => s - 1)}
                disabled={step === 0}
                className="flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium disabled:opacity-0 transition-colors"
              >
                <ArrowLeft size={15} /> Back
              </button>
              {step < 3 ? (
                <button
                  data-testid="apply-next-btn"
                  onClick={() => canNext && setStep((s) => s + 1)}
                  disabled={!canNext}
                  className="bg-safety hover:bg-safety-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-7 py-3.5 flex items-center gap-2 transition-colors"
                >
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  data-testid="apply-submit-btn"
                  onClick={submit}
                  disabled={!canNext || submitting}
                  className="bg-safety hover:bg-safety-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-7 py-3.5 transition-colors"
                >
                  {submitting ? "Submitting…" : "Submit application"}
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
